// Save/load orchestration for the EfficiencyTests authoring flow — the EfficiencyTests-native
// counterpart of ScoringProfiles' own `scoringProfileEditor.js`. That module cannot be reused
// as-is: it dispatches/reads internally against `scoringProfilesCommon`/`scoringProfilesFilters`/
// `scoringProfilesDefinition`/`scoringProfilesCoverageDetail` and posts to `/api/scoringProfiles/*`
// — pointing EfficiencyTests at it would silently save an efficiency test as a scoring profile.
//
// POST /api/efficiencyTests/save request contract confirmed by the user (full JSON sample) as:
// { id, testedProductId, criteria: [{key, value: <object>}], excludedProducts: [...], includedProducts: [...],
//   name, description, scoringProfileModelId, driversSorting: [...], scoringDefinition: {...},
//   passThresholdPercent, status, outcome, createdBy, createdAt, lastUpdatedBy, lastUpdatedAt,
//   lastExecutedAt, lastError }. The trailing status/outcome/createdBy/.../lastError fields read as
//   server-managed audit fields on the entity (the same swagger sample almost certainly doubles as
//   the GET response shape) — NOT sent on save. `scoringDefinition` is deliberately NOT sent either:
//   a scoring profile model is now mandatory (TabScoringDefinition.vue's "Personalizzato"/custom
//   option was removed), so the model's own driver weights fully determine scoring server-side —
//   scoringProfileModelId + driversSorting is enough.
//
// passThresholdPercent is picked via TabScoringDefinition.vue's 0-100 slider
// (efficiencyTestsDefinition/getPassThresholdPercent), not hardcoded.
// GET /api/efficiencyTests/{id} response contract was NOT given — loadScoringProfile below assumes
// it mirrors the save request field-for-field; verify once confirmed. It also only returns
// `testedProductId` (no full product object): the editor stores that bare id, and
// EfficiencyTestsEdit.vue completes it (name and Characteristics sidebar fields, as
// EfficiencyTestsCreate.vue's productField() reads them) via efficiencyTestsCommon/loadSelectedProductById.
import { locale } from '../../../imports/i18n'
import { get as _get, post as _post } from '../../../utils/fetcher'
import { mapScoringProfileDefinition } from '../../../utils/drivers'
import { INIT_DEFINITION_SETTINGS } from '../../../settings/scoringProfilesSettings'
import { cloneObject } from '../../../imports/lib'
import { criteriaToApi, criterionValueFromApi } from '../../../utils/efficiencyTestCriteria'

export default {
  namespaced: true,

  actions: {
    // `criteria` is ComparisonCriteria.vue's own getCriteriaPayload() output, read by the caller via
    // $refs (see EfficiencyTestsCreate.vue/EfficiencyTestsEdit.vue's saveScoringProfile()) since
    // criteria lives in that component's local state, not Vuex.
    saveScoringProfile({ dispatch, rootGetters }, criteria) {
      dispatch('efficiencyTestsCommon/setLoading', true, { root: true })

      const selectedProduct = rootGetters['efficiencyTestsCommon/getSelectedProduct']
      // The picked scoringProfileModelId — TabScoringDefinition.vue's model dropdown no longer
      // offers a "Personalizzato"/custom option, so this is always expected to be set once the user
      // has picked a model (0 falls back to the "no model" sentinel below, e.g. before any pick).
      const selectedModelId = rootGetters['efficiencyTestsDefinition/getScoringProfileDefinitionSelectValue']
      const driversSorting = rootGetters['efficiencyTestsDefinition/getSortedModelDriversToSendToAPI']

      const profile = {
        id: rootGetters['efficiencyTestsCommon/getScoringProfileId'] || 0,
        testedProductId: selectedProduct && selectedProduct.productId ? selectedProduct.productId : 0,
        criteria: criteriaToApi(criteria),
        excludedProducts: rootGetters['efficiencyTestsCoverageDetail/getCoverageManualExcluded'],
        includedProducts: rootGetters['efficiencyTestsCoverageDetail/getCoverageManualIncluded'],
        name: rootGetters['efficiencyTestsCommon/getScoringProfileName'],
        description: rootGetters['efficiencyTestsCommon/getScoringProfileDescription'],
        // Backend field is a non-nullable Int32 — 0 is the "no model" sentinel (matches the swagger
        // sample), not null (that's what threw the earlier JSON deserialization error).
        scoringProfileModelId: selectedModelId || 0,
        // Sorted driver names from the picked model's driver list — matches scoringProfileEditor.js's
        // own field wiring, not hardcoded.
        driversSorting: driversSorting,
        passThresholdPercent: rootGetters['efficiencyTestsDefinition/getPassThresholdPercent'],
      }

      return _post('/api/efficiencyTests/save', profile).finally(() => {
        dispatch('efficiencyTestsCommon/setLoading', false, { root: true })
      })
    },

    // Returns the loaded `criteria` array (already in ComparisonCriteria.vue's own { key, value: {}
    // } shape — see that component's setCriteria()) so the caller can hand it to the ref after this
    // resolves; the rest of the response is committed straight into Vuex the same way
    // scoringProfileEditor.js's loadScoringProfile does.
    loadScoringProfile({ commit, dispatch, rootGetters }, params) {
      dispatch('efficiencyTestsCommon/setLoading', true, { root: true })
      commit('efficiencyTestsCommon/setIsProfileCoverageProvidedByAPI', true, { root: true })
      dispatch('efficiencyTestsDefinition/resetSelectedModel', null, { root: true })

      return _get(`/api/efficiencyTests/${params.id}`)
        .then((data) => {
          // A Ready, InProgress or WaitingResources test is not editable (duplicating it is fine): reject before
          // anything is committed, so the editor never shows it as if it were.
          if (params.action !== 'duplicate' && ['Ready', 'InProgress', 'WaitingResources'].includes(data.status)) {
            return Promise.reject({ readyNotEditable: true })
          }
          let id = params.id
          let name = data.name
          if (params.action === 'duplicate') {
            id = null
            name = data.name + ' - ' + (locale() === 'it' ? 'Copia' : 'Copy')
            dispatch('efficiencyTestsCommon/setIsDuplicate', true, { root: true })
          }
          dispatch('efficiencyTestsCommon/setScoringProfileId', id, { root: true })
          dispatch('efficiencyTestsCommon/setScoringProfileName', name, { root: true })
          dispatch('efficiencyTestsCommon/setScoringProfileDescription', data.description || '', {
            root: true,
          })

          // GET only returns testedProductId (no full product object) — see the file-header note.
          // Store what we have so re-saving round-trips the id; the view then loads the rest.
          if (data.testedProductId) {
            dispatch(
              'efficiencyTestsCommon/setSelectedProduct',
              { productId: data.testedProductId },
              { root: true }
            )
          }

          // The reasons of the last execution of a Failed test, for the peer tab of the draft. Kept as they
          // came: what applies to the draft is worked out against its peers when they are shown.
          commit(
            'efficiencyTestsCoverageDetail/setLastExecution',
            { failed: data.status === 'Failed', failures: data.failures || [] },
            { root: true }
          )

          commit('efficiencyTestsCoverageDetail/setCoverageManualExcluded', data.excludedProducts || [], {
            root: true,
          })
          commit('efficiencyTestsCoverageDetail/setCoverageManualIncluded', data.includedProducts || [], {
            root: true,
          })

          // scoringDefinition is no longer sent on save (see file header), so the GET may return it
          // null — reading .weightType off it used to throw here and abort everything below
          // (model, drivers and pass threshold were never restored).
          if (data.scoringDefinition) {
            dispatch('efficiencyTestsDefinition/setSelectedWeightType', data.scoringDefinition.weightType, {
              root: true,
            })
            dispatch('efficiencyTestsDefinition/setDefaultSelectedWeightType', data.scoringDefinition.weightType, {
              root: true,
            })
            dispatch(
              'efficiencyTestsDefinition/setSequenceWeightsDecay',
              data.scoringDefinition.geometricDrivers.sequenceWeightsDecay,
              { root: true }
            )
            dispatch(
              'efficiencyTestsDefinition/setDefaultSequenceWeightsDecay',
              data.scoringDefinition.geometricDrivers.sequenceWeightsDecay,
              { root: true }
            )

            const scoringDefinitionSettings = mapScoringProfileDefinition(
              INIT_DEFINITION_SETTINGS(),
              data.scoringDefinition
            )

            dispatch('efficiencyTestsDefinition/setScoringDefinitionSettings', scoringDefinitionSettings, {
              root: true,
            })
            dispatch(
              'efficiencyTestsDefinition/setDefaultScoringDefinitionSettings',
              cloneObject(scoringDefinitionSettings),
              { root: true }
            )
          }

          // Restore the picked model — see TabScoringDefinition.vue's model dropdown and
          // saveScoringProfile's mirrored read above.
          dispatch(
            'efficiencyTestsDefinition/setScoringProfileDefinitionSelectValue',
            data.scoringProfileModelId || 0,
            { root: true }
          )

          // `??`-style: a saved 0% threshold must stay 0, not fall back to the default.
          dispatch(
            'efficiencyTestsDefinition/setPassThresholdPercent',
            data.passThresholdPercent != null ? data.passThresholdPercent : 100,
            { root: true }
          )

          const criteria = criteriaFromApi(data.criteria)

          // The select value alone only highlights the dropdown: the drivers editor is driven by
          // selectedModelId + the model's driver lists, which must be fetched and re-sorted.
          if (!data.scoringProfileModelId) return criteria
          return dispatch(
            'efficiencyTestsDefinition/loadSelectedModelFromAPI',
            { modelId: data.scoringProfileModelId, driversSorting: data.driversSorting },
            { root: true }
          ).then(() => criteria)
        })
        .finally(() => {
          dispatch('efficiencyTestsCommon/setLoading', false, { root: true })
          commit('efficiencyTestsCommon/setIsProfileCoverageProvidedByAPI', false, { root: true })
        })
    },
  },
}

// GET /api/efficiencyTests/{id} returns criteria as stored: snake_case fields, converted back to
// ComparisonCriteria.vue's camelCase (see utils/efficiencyTestCriteria). Falls back to JSON.parse for
// a value still stored as a string by an older client. Unparseable entries are skipped rather than
// crashing the load.
function criteriaFromApi(criteria) {
  return (criteria || [])
    .map((c) => {
      if (typeof c.value !== 'string') {
        return { key: c.key, value: criterionValueFromApi(c.value) }
      }
      try {
        return { key: c.key, value: criterionValueFromApi(JSON.parse(c.value)) }
      } catch (e) {
        return null
      }
    })
    .filter((c) => c != null)
}
